import type { HopTestResult, Member } from "../cloud/api";

/** One rendered line of the live hop test (what the page shows per hop). */
export interface HopRow {
  hop: number;
  expectedPosition: number;
  expectedNumber: string | undefined;
  rangPosition: number | null;
  rangNumber: string | undefined;
  forwardedFrom: number | null;
  timeToRingMs: number | null;
  ok: boolean;
  /** `pending` until the cloud has filled the step in; then `ok` / `wrong_member` / `no_ring`. */
  outcome: "pending" | "ok" | "wrong_member" | "no_ring";
}

export function isHopTestTerminal(r: Pick<HopTestResult, "state">): boolean {
  return r.state === "done" || r.state === "failed" || r.state === "passed";
}

export function hopTestPassed(r: HopTestResult): boolean {
  if (!isHopTestTerminal(r) || r.state === "failed") return false;
  if (r.steps.length === 0) return false;
  if (!r.steps.every((s) => s.ok)) return false;
  return r.no_reply ? r.no_reply.ok : true;
}

export function hopRows(r: HopTestResult, members: readonly Pick<Member, "position" | "number">[]): HopRow[] {
  const num = (p: number | null | undefined) => (p === null || p === undefined ? undefined : members.find((m) => m.position === p)?.number);
  return r.steps.map((s) => {
    let outcome: HopRow["outcome"];
    if (s.ok) outcome = "ok";
    else if (s.rang_member === null && isHopTestTerminal(r)) outcome = "no_ring";
    else if (s.rang_member === null) outcome = "pending";
    else outcome = "wrong_member";
    return {
      hop: s.hop,
      expectedPosition: s.expected_member,
      expectedNumber: num(s.expected_member),
      rangPosition: s.rang_member,
      rangNumber: num(s.rang_member),
      forwardedFrom: s.forwarded_from,
      timeToRingMs: s.time_to_ring_ms,
      ok: s.ok,
      outcome,
    };
  });
}

export function fmtMs(ms: number | null): string {
  if (ms === null) return "—";
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${ms} ms`;
}
